/**
 * Chore tracking and management
 */

import {
  API_ENDPOINTS,
  CONFETTI_DURATION_MS,
  CONFETTI_PARTICLE_COUNT,
  CONFETTI_SIDE_PARTICLE_COUNT,
  CHORE_REFRESH_DELAY_MS,
} from '../config.js';
import { getCsrfToken, setLastCheck } from './state.js';
import { getChoreCountdown } from '../utils/time.js';
import { sortChoresWithIndex, separateChoresByCompletion } from '../utils/dom.js';
import { updateHealthStatus } from './updates.js';

/**
 * Update the chore list display
 */
export function updateChore() {
  // Set loading state
  jQuery('#wsStatusChore').removeClass('success alert').addClass('secondary');

  jQuery.getJSON(API_ENDPOINTS.CHORE, {}, (data) => {
    if (data.error) {
      console.error('[Chores] Error loading chores:', data.error);
      jQuery('#chore-list').html('<p class="text-center alert">Failed to load chores</p>');
      jQuery('#wsStatusChore').removeClass('secondary success').addClass('alert');
      return;
    }

    // Create optimized 3-column layout for tablet display
    let choreHTML = `<div class="chore-header">Chores for ${data.day}</div>`;
    choreHTML += '<div class="chore-container">';

    // Create columns for each child
    jQuery.each(data.children, (childName, childData) => {
      choreHTML += '<div class="chore-column">';
      choreHTML += `<div class="chore-child-name">${childData.displayName}</div>`;
      choreHTML += '<div class="chore-items">';

      // Sort and separate chores
      const sortedWithIndex = sortChoresWithIndex(childData.chores);
      const { incomplete, completed } = separateChoresByCompletion(sortedWithIndex);

      // Render incomplete chores first
      jQuery.each(incomplete, (index, item) => {
        const chore = item.chore;
        const originalIndex = item.originalIndex;
        let choreClass = 'chore-pending';
        let countdownText = '';
        let urgencyIcon = '';

        // Calculate countdown if dueTime exists
        if (chore.dueTime) {
          const countdown = getChoreCountdown(chore.dueTime);

          if (countdown.isOverdue) {
            choreClass = 'chore-overdue';
            urgencyIcon = '<span class="chore-icon">⚠️</span>';
          } else if (countdown.isUrgent) {
            choreClass = 'chore-urgent';
            urgencyIcon = '<span class="chore-icon">⏰</span>';
          }

          const hasIcon = urgencyIcon !== '';
          const timeClass = hasIcon ? 'chore-time with-icon' : 'chore-time';
          countdownText = `<span class="${timeClass}">${countdown.text}</span>`;
        }

        choreHTML += '<div class="chore-item-wrapper">';
        choreHTML += `<button class="chore-btn ${choreClass}" `;
        choreHTML += `data-child="${childName}" `;
        choreHTML += `data-index="${originalIndex}">`;
        choreHTML += '<div class="chore-content">';
        choreHTML += '<div class="chore-main-line">';
        choreHTML += `${urgencyIcon}<span class="chore-name">${chore.name}</span>`;
        choreHTML += '</div>';
        if (countdownText) {
          choreHTML += countdownText;
        }
        choreHTML += '</div>';
        choreHTML += '</button>';
        choreHTML += '</div>';
      });

      // Render completed chores more compactly
      if (completed.length > 0) {
        choreHTML += '<div class="chore-completed-section">';
        jQuery.each(completed, (index, item) => {
          choreHTML += '<div class="chore-completed">';
          choreHTML += `✓ ${item.chore.name}`;
          choreHTML += '</div>';
        });
        choreHTML += '</div>';
      }

      // Check if all chores are complete
      const allComplete = incomplete.length === 0 && completed.length > 0;

      choreHTML += '</div>'; // Close chore-items

      // Add special class if all chores are complete
      if (allComplete) {
        choreHTML = choreHTML.replace('class="chore-column"', 'class="chore-column all-complete"');
      }

      choreHTML += '</div>'; // Close chore-column
    });

    choreHTML += '</div>'; // Close chore-container

    jQuery('#chore-list').html(choreHTML);
    jQuery('#wsUpdateChore').text(new Date().toLocaleTimeString());
    jQuery('#wsStatusChore').removeClass('secondary alert').addClass('success');
    setLastCheck('Chore', new Date());

    // Update overall health status
    setTimeout(() => updateHealthStatus(), 50);

  }).fail((xhr, status, error) => {
    console.error('[Chores] Error fetching chores:', error);
    jQuery('#chore-list').html('<p class="text-center alert">Error loading chores</p>');
    jQuery('#wsStatusChore').removeClass('secondary success').addClass('alert');

    // Update overall health status
    setTimeout(() => updateHealthStatus(), 50);
  });
}

/**
 * Populate the chore reset modal with completed chores
 */
export function populateChoreResetModal() {
  jQuery.getJSON(API_ENDPOINTS.CHORE, {}, (data) => {
    if (data.error) {
      console.error('[Chores] Error loading chores:', data.error);
      jQuery('#chore-reset-list').html('<p class="text-center alert">Failed to load chores</p>');
      return;
    }

    let choreHTML = '';

    jQuery.each(data.children, (childName, childData) => {
      // Sort and separate chores
      const sortedWithIndex = sortChoresWithIndex(childData.chores);
      const { completed } = separateChoresByCompletion(sortedWithIndex);

      // Only show children with completed chores
      if (completed.length > 0) {
        choreHTML += `<h4>${  childData.displayName  }</h4>`;
        choreHTML += '<div class="grid-x grid-padding-x small-up-2">';

        jQuery.each(completed, (index, item) => {
          const chore = item.chore;
          const originalIndex = item.originalIndex;

          choreHTML += '<div class="cell">';
          choreHTML += '<button class="button alert expanded chore-reset-btn" ';
          choreHTML += `data-child="${  childName  }" `;
          choreHTML += `data-index="${  originalIndex  }">`;
          choreHTML += `✓ ${  chore.name}`;
          choreHTML += '</button>';
          choreHTML += '</div>';
        });

        choreHTML += '</div>';
      }
    });

    if (choreHTML === '') {
      choreHTML = '<p class="text-center">No completed chores to reset.</p>';
    }

    jQuery('#chore-reset-list').html(choreHTML);
  }).fail((xhr, status, error) => {
    console.error('[Chores] Error fetching chores:', error);
    jQuery('#chore-reset-list').html('<p class="text-center alert">Error loading chores</p>');
  });
}

/**
 * Trigger confetti animation
 */
function triggerConfetti() {
  const end = Date.now() + CONFETTI_DURATION_MS;

  (function frame() {
    confetti({
      particleCount: CONFETTI_SIDE_PARTICLE_COUNT,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
    });
    confetti({
      particleCount: CONFETTI_SIDE_PARTICLE_COUNT,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
    });

    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  }());

  // Big center burst
  confetti({
    particleCount: CONFETTI_PARTICLE_COUNT,
    spread: 360,
    origin: {
      x: 0.5,
      y: 0.5,
    },
  });
}

/**
 * Display success notification to user
 * @param {string} message - Success message to display
 */
function showSuccessNotification(message) {
  // Create success notification element if it doesn't exist
  if (jQuery('#success-notification').length === 0) {
    jQuery('body').append('<div id="success-notification" class="callout success" style="position: fixed; top: 10px; right: 10px; z-index: 9999; max-width: 400px; display: none;"></div>');
  }

  jQuery('#success-notification').html(`<strong>Success:</strong> ${  message}`).fadeIn().delay(3000).fadeOut();
}

/**
 * Display error notification to user
 * @param {string} message - Error message to display
 */
function showErrorNotification(message) {
  // Create error notification element if it doesn't exist
  if (jQuery('#error-notification').length === 0) {
    jQuery('body').append('<div id="error-notification" class="callout alert" style="position: fixed; top: 10px; right: 10px; z-index: 9999; max-width: 400px; display: none;"></div>');
  }

  jQuery('#error-notification').html(`<strong>Error:</strong> ${  message}`).fadeIn().delay(5000).fadeOut();
}

/**
 * Set up chore event handlers
 */
export function setupChoreHandlers() {
  // Complete chore button handler
  jQuery(document).on('click', '.chore-btn', function () {
    const button = jQuery(this);
    const child = button.data('child');
    const index = button.data('index');
    const choreName = button.find('.chore-name').text() || 'Chore';

    // Disable button during request
    button.prop('disabled', true).addClass('disabled');

    jQuery.post(API_ENDPOINTS.CHORE_COMPLETE, {
      child: child,
      index: index,
      _csrf_token: getCsrfToken(),
    }, (response) => {
      if (response.success) {
        triggerConfetti();
        showSuccessNotification(`${choreName  } completed!`);

        // Refresh the chore list to move completed chore to the bottom section
        setTimeout(() => {
          updateChore();
        }, CHORE_REFRESH_DELAY_MS);
      } else {
        console.error('[Chores] Failed to complete chore:', response.error);
        showErrorNotification(`Failed to complete chore: ${  response.error || 'Unknown error'}`);
        button.prop('disabled', false).removeClass('disabled');
      }
    }).fail((xhr, status, error) => {
      console.error('[Chores] Error completing chore:', error);
      showErrorNotification('Error completing chore. Please try again.');
      button.prop('disabled', false).removeClass('disabled');
    });
  });

  // Uncomplete chore button handler
  jQuery(document).on('click', '.chore-reset-btn', function () {
    const button = jQuery(this);
    const child = button.data('child');
    const index = button.data('index');
    const choreName = button.text().replace('✓ ', '').trim();

    // Disable button during request
    button.prop('disabled', true).addClass('disabled');

    jQuery.post(API_ENDPOINTS.CHORE_UNCOMPLETE, {
      child: child,
      index: index,
      _csrf_token: getCsrfToken(),
    }, (response) => {
      if (response.success) {
        showSuccessNotification(`${choreName  } reset!`);
        // Remove button from modal
        button.parent().remove();
        // Refresh the main chore list
        updateChore();
      } else {
        console.error('[Chores] Failed to reset chore:', response.error);
        showErrorNotification(`Failed to reset chore: ${  response.error || 'Unknown error'}`);
        button.prop('disabled', false).removeClass('disabled');
      }
    }).fail((xhr, status, error) => {
      console.error('[Chores] Error resetting chore:', error);
      showErrorNotification('Error resetting chore. Please try again.');
      button.prop('disabled', false).removeClass('disabled');
    });
  });
}
